import { ComponentProps } from 'react';
import { _js } from '@ifixit/utils/localize';
import { OrderPartApiButton } from '../THD/thdApiButtons';
import { fromWiki } from '../Generic/fromWiki';
import { iFixitHotSpotItem } from '../Generic/iFixitHotSpotItem';
import { cn } from '@ifixit/ui/lib/utils';

type ButtonProps = ComponentProps<'button'>;

const OrderPartButton = ({
   data,
   className,
   ...props
}: {
   data: iFixitHotSpotItem;
} & ButtonProps) => {
   const isIfixitPart = fromWiki(data, 'isIfixitPart') || false;
   const sku = fromWiki(data, 'sku');

   if (!sku) {
      return null;
   }
   const apiButton = <OrderPartApiButton data={data} className={className} {...props} />;

   if (apiButton) {
      return apiButton;
   }

   if (isIfixitPart) {
      /**
       * When swapping to our stores on the same frontend, we swapped from carts being on iFixit
       * to carts being controlled by Shopify. We did not update this call site.
       *
       * The main thing seems to be that we need the CartLineItem, not just the sku.
       * Here is a pull with most of the implementation needed
       *
       * https://github.com/iFixit/ifixit/pull/54952
       */
      const addToCartWorksInSchematics = false;
      return addToCartWorksInSchematics ? (
         <AddToCartButton data={data} className={className} {...props} />
      ) : null;
   }

   return null;
};

const AddToCartButton = ({
   data,
   className,
   ...props
}: {
   data: iFixitHotSpotItem;
} & ButtonProps) => {
   const addToCart = () => {
      if (!data.wiki || !data.wiki.sku) {
         return;
      }

      const _quantity = 1;
      const _itemcode = data.wiki.sku;
      // addItemcode(itemcode, quantity);
   };

   return (
      <button
         onClick={addToCart}
         className={cn(
            'bg-brand-500 text-white font-semibold py-2 px-4 rounded-md mt-4 mb-2.5 hover:bg-brand-600',
            className
         )}
         {...props}
      >
         {_js('Order Part')}
      </button>
   );
};

type ViewGuidesButtonProps = {
   data: iFixitHotSpotItem;
} & Omit<ComponentProps<'a'>, 'href'>;

const ViewGuidesButton = ({ data, className, ...props }: ViewGuidesButtonProps) => {
   const hasWikiUrl = !!fromWiki(data, 'url');
   const hasGuides = !!fromWiki(data, 'hasAssociatedGuides');

   const showButton = hasWikiUrl && hasGuides;

   if (!showButton) {
      return null;
   }

   return (
      <a
         href={data.wiki?.url ?? ''}
         target="_blank"
         rel="noopener noreferrer"
         className={cn(
            'inline-flex items-center justify-center border border-gray-300 rounded-md px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-100 no-underline',
            className
         )}
         {...props}
      >
         {_js('View Guides')}
      </a>
   );
};

export { ViewGuidesButton, OrderPartButton };
